import React, { useRef, useState, useCallback } from 'react';
import { X, Camera, RotateCcw, Check, Upload, Loader } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

export default function CupomCapture({ proposalId, onClose, onItemsAdded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setStream(s);
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      toast.error('Não foi possível acessar a câmera. Use a opção de upload.');
    }
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    setCameraActive(false);
  }, [stream]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCapturedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const processOCR = async () => {
    if (!capturedImage) return;
    setProcessing(true);
    try {
      const res = await api.post('/ocr/cupom', { image_base64: capturedImage });
      const dados = res.data.dados;
      setOcrResult(dados);
      // Pre-select all items
      setSelectedItems((dados.itens || []).map((_, idx) => idx));
      toast.success(`OCR concluído! ${dados.itens?.length || 0} itens encontrados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar OCR');
    } finally {
      setProcessing(false);
    }
  };

  const toggleItem = (idx) => {
    setSelectedItems(p =>
      p.includes(idx) ? p.filter(i => i !== idx) : [...p, idx]
    );
  };

  const confirmItems = async () => {
    if (!ocrResult) return;
    const items = (ocrResult.itens || []).filter((_, idx) => selectedItems.includes(idx));
    
    // Save cupom to proposal if we have a proposalId
    let cupomData = {
      id: Date.now().toString(),
      nome_arquivo: `cupom_${new Date().toISOString().slice(0, 10)}.jpg`,
      imagem_base64: capturedImage,
      dados_extraidos: ocrResult,
      data: new Date().toISOString()
    };
    
    if (proposalId) {
      try {
        await api.post(`/proposals/${proposalId}/cupom`, {
          nome_arquivo: cupomData.nome_arquivo,
          imagem_base64: capturedImage,
          dados_extraidos: ocrResult
        });
      } catch (err) {
        console.error('Error saving cupom:', err);
      }
    }
    
    onItemsAdded(items, cupomData);
  };

  const reset = () => {
    setCapturedImage(null);
    setOcrResult(null);
    setSelectedItems([]);
    stopCamera();
  };

  const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="cupom-capture-modal">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
        <h2 className="font-heading font-semibold text-white">Capturar Cupom Fiscal</h2>
        <button onClick={() => { stopCamera(); onClose(); }} className="text-zinc-400 hover:text-white p-2">
          <X size={20} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Step 1: Capture */}
        {!capturedImage && !cameraActive && (
          <div className="space-y-3">
            <p className="text-zinc-400 text-sm">Escolha como capturar o cupom:</p>
            <button
              data-testid="start-camera-btn"
              onClick={startCamera}
              className="w-full flex items-center justify-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-orange-500/50 text-white py-4 rounded-xl transition-all"
            >
              <Camera size={22} className="text-orange-400" />
              <span className="font-medium">Abrir câmera</span>
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 py-4 rounded-xl transition-all"
            >
              <Upload size={20} />
              <span>Fazer upload de imagem</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
          </div>
        )}

        {/* Camera view */}
        {cameraActive && !capturedImage && (
          <div className="relative rounded-xl overflow-hidden bg-black">
            <video ref={videoRef} className="w-full" playsInline muted />
            <div className="absolute inset-4 border-2 border-orange-400/60 rounded-lg pointer-events-none">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-orange-400 rounded-tl" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-orange-400 rounded-tr" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-orange-400 rounded-bl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-orange-400 rounded-br" />
            </div>
            <p className="absolute bottom-4 left-0 right-0 text-center text-xs text-orange-300">
              Posicione o cupom dentro da moldura
            </p>
          </div>
        )}

        {/* Step 2: Preview */}
        {capturedImage && !ocrResult && (
          <div className="relative">
            <img src={capturedImage} alt="Cupom" className="w-full rounded-xl border border-zinc-700 max-h-72 object-contain bg-zinc-900" />
          </div>
        )}

        {/* Step 3: OCR Result */}
        {ocrResult && (
          <div className="space-y-4">
            <div className="bg-zinc-800 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">{ocrResult.estabelecimento || 'Estabelecimento'}</span>
                <span className="text-xs text-zinc-500">{ocrResult.data}</span>
              </div>
              <div className="text-xs text-zinc-500">Total identificado: <span className="text-orange-400 font-mono">{brl(ocrResult.total)}</span></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-white">Selecione os itens para adicionar:</p>
                <button onClick={() => setSelectedItems(ocrResult.itens?.map((_, i) => i) || [])}
                  className="text-xs text-orange-400 hover:text-orange-300">Todos</button>
              </div>
              <div className="space-y-2">
                {(ocrResult.itens || []).map((item, idx) => (
                  <label key={idx} className="flex items-center gap-3 bg-zinc-800 rounded-lg p-3 cursor-pointer hover:bg-zinc-700 transition-colors">
                    <input type="checkbox" checked={selectedItems.includes(idx)} onChange={() => toggleItem(idx)} className="accent-orange-500 w-4 h-4" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{item.descricao}</div>
                      <div className="text-xs text-zinc-500">{item.quantidade} {item.unidade}</div>
                    </div>
                    <span className="font-mono text-xs text-orange-400 shrink-0">{brl(item.valor_total)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed footer with action buttons */}
      <div className="shrink-0 p-4 border-t border-zinc-800 bg-zinc-900/95 space-y-2">
        {/* Camera active */}
        {cameraActive && !capturedImage && (
          <div className="flex gap-3">
            <button onClick={stopCamera} className="flex items-center gap-2 border border-zinc-700 text-zinc-400 hover:text-white px-4 py-3 rounded-xl text-sm transition-colors">
              Cancelar
            </button>
            <button data-testid="capture-photo-btn" onClick={capturePhoto}
              className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold py-3 rounded-xl transition-colors">
              <Camera size={18} />
              Capturar foto
            </button>
          </div>
        )}

        {/* Preview - process OCR */}
        {capturedImage && !ocrResult && (
          <div className="flex gap-3">
            <button onClick={reset} className="flex items-center gap-2 border border-zinc-700 text-zinc-400 hover:text-white px-4 py-3 rounded-xl text-sm transition-colors">
              <RotateCcw size={14} />
              Refazer
            </button>
            <button data-testid="process-ocr-btn" onClick={processOCR} disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors">
              {processing ? <><Loader size={16} className="animate-spin" /> Processando...</> : <><Check size={16} /> Extrair dados com IA</>}
            </button>
          </div>
        )}

        {/* OCR done - confirm items */}
        {ocrResult && (
          <div className="flex gap-3">
            <button onClick={reset} className="flex items-center gap-2 border border-zinc-700 text-zinc-400 hover:text-white px-4 py-3 rounded-xl text-sm transition-colors">
              <RotateCcw size={14} />
              Refazer
            </button>
            <button data-testid="confirm-ocr-items-btn" onClick={confirmItems} disabled={selectedItems.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors">
              <Check size={16} />
              Adicionar {selectedItems.length} iten{selectedItems.length !== 1 ? 's' : ''}
            </button>
          </div>
        )}

        {/* Initial state */}
        {!capturedImage && !cameraActive && !ocrResult && (
          <button onClick={() => { stopCamera(); onClose(); }} className="w-full border border-zinc-700 text-zinc-400 hover:text-white py-3 rounded-xl text-sm transition-colors">
            Fechar
          </button>
        )}
      </div>

      {/* Hidden canvas */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
